import {
  WSFilterApiOptionsLoader,
  WSFilterOption
} from "@wingspanhq/fe-component-library";
import { PayeeRowsApiSearchPayeeRowsRequest } from "@wingspanhq/wingspan-api-sdk";
import { useMemo, useState } from "react";
import { queriesPayeeRows } from "@query";
import { selectorSearchRowName } from "../../../shared/selectors/selectorSearchRowName";

/**
 * Custom hook for providing API-based payee filter options
 * with pagination and search support.
 *
 * @returns WSFilterApiOptionsLoader configuration for WSFilters
 *
 * @example
 * const payeeOptions = usePayeeFilterOptions();
 *
 * const filters: WSFilter[] = [
 *   {
 *     title: "Contractor",
 *     name: "payeeId",
 *     type: "select",
 *     apiOptions: payeeOptions
 *   }
 * ];
 */

export function usePayeeFilterOptions(
  baseQuery?: PayeeRowsApiSearchPayeeRowsRequest
): WSFilterApiOptionsLoader {
  const [searchText, setSearchText] = useState("");

  const query = queriesPayeeRows.searchPayeeRows.usePaginatedQuery({
    ...baseQuery,
    filter: {
      ...baseQuery?.filter,
      searchString: searchText ? { eq: searchText } : undefined
    }
  });

  const options = useMemo(() => {
    return (query.flatData || [])
      .map(payee => {
        if (!payee.id) return null;

        return {
          name: payee.id,
          label: selectorSearchRowName(payee),
          helperText: payee.email ?? undefined
        };
      })
      .filter(Boolean) as WSFilterOption<"checkbox">[];
  }, [query.flatData]);

  return {
    data: options,
    isLoading: query.isInitialLoading,
    pagination: {
      hasNextPage: query.hasNextPage ?? false,
      isFetching: query.isFetching,
      isFetchingNextPage: query.isFetchingNextPage,
      fetchNextPage: query.fetchNextPage
    },
    search: {
      searchText,
      onSearchChange: setSearchText
    }
  };
}
